import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Moon, BookOpen, Wine, Menu, LogIn } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { useThemeStore, type ThemeName } from '../../stores/theme.store.js';

const themeOptions: { value: ThemeName; icon: typeof Moon; label: string; color: string }[] = [
  { value: 'nocturna', icon: Moon, label: 'Nocturna', color: 'bg-[#7c5cff]' },
  { value: 'crema', icon: BookOpen, label: 'Crema Editorial', color: 'bg-[#c75c2a]' },
  { value: 'burgundy', icon: Wine, label: 'Burgundy Velvet', color: 'bg-[#c44569]' },
];

export function Navbar() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const { theme, setTheme } = useThemeStore();
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-16 border-b border-border bg-surface/80 backdrop-blur-xl">
      <div className="flex h-full items-center gap-4 px-4">
        {/* Mobile menu button */}
        <button className="lg:hidden p-2 rounded-lg hover:bg-surface-hover text-text-secondary">
          <Menu size={20} />
        </button>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="text-xl font-bold text-primary">LyricsHub</span>
        </Link>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex-1 max-w-xl mx-auto">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar canciones, artistas, álbumes..."
              className="w-full h-10 pl-10 pr-4 rounded-xl bg-background border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            />
          </div>
        </form>

        {/* Theme switcher */}
        <div className="relative">
          <button
            onClick={() => setShowThemeMenu(!showThemeMenu)}
            className="flex items-center gap-1.5 p-2 rounded-lg hover:bg-surface-hover transition-colors"
          >
            {themeOptions.map((opt) => (
              <span
                key={opt.value}
                className={cn(
                  'w-4 h-4 rounded-full transition-all',
                  opt.color,
                  theme === opt.value
                    ? 'ring-2 ring-offset-2 ring-offset-surface ring-primary scale-110'
                    : 'opacity-50',
                )}
              />
            ))}
          </button>

          {showThemeMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowThemeMenu(false)} />
              <div className="absolute right-0 top-full mt-2 z-20 bg-surface border border-border rounded-xl shadow-lg p-2 min-w-[160px]">
                {themeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setTheme(opt.value);
                      setShowThemeMenu(false);
                    }}
                    className={cn(
                      'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm transition-colors',
                      theme === opt.value
                        ? 'bg-primary/10 text-primary'
                        : 'text-text-secondary hover:bg-surface-hover',
                    )}
                  >
                    <span className={cn('w-3 h-3 rounded-full', opt.color)} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Login button */}
        <Link
          to="/login"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-hover transition-colors"
        >
          <LogIn size={16} />
          <span className="hidden sm:inline">Entrar</span>
        </Link>
      </div>
    </header>
  );
}
